import { useRef } from 'react';
import { useActivePractice, useDispatch } from '@witmetrics/data-store';
import { useToggle } from '@/hooks/useToggle';
import { useAppState } from '@/providers/AppStateProvider';
import { useLoadedFiles } from '@/providers/LoadedFilesProvider';
import { useArray } from '@/hooks/useArray';
import IconButton from '@/components/IconButton';
import { AddIcon } from '@/icons';
import NewDocumentMenu from '@/menus/NewDocumentMenu';
import TraceRecordDialog from '@/dialogs/TraceRecordDialog';
import UploadToaster from '@/toasters/UploadToaster';
import { uploadDocument } from './utils';

export type NewDocumentMenuButtonProps = {
  fileCollectionId?: number;
  onCreateCollection: () => Promise<number>;
  onSaveDocument?: (fileId: number) => void;
};

export default function NewDocumentMenuButton({
  fileCollectionId,
  onCreateCollection,
  onSaveDocument,
}: NewDocumentMenuButtonProps) {
  const menuToggleRef = useRef(null);
  const dispatch = useDispatch();
  const { onApiError, onToggleUnloadWarning } = useAppState();
  const activePractice = useActivePractice();
  const { addLoadedFile } = useLoadedFiles();
  const [uploads, setUploads, { clear: clearUploads }] = useArray();
  const [isMenuOpen, toggleMenu] = useToggle();
  const [isUploadToasterOpen, toggleUploadToaster] = useToggle();
  const [isTraceRecordOpen, toggleTraceRecord] = useToggle();

  const handleFileUpload = async (file: File) => {
    try {
      // Older cases might not have a Documents collection so need to instantiate one
      let collectionId = fileCollectionId;
      if (!collectionId) collectionId = await onCreateCollection();
      setUploads([file.name]);
      toggleUploadToaster(true);
      onToggleUnloadWarning(true);
      const data = await uploadDocument(activePractice!.id, collectionId, file);
      onToggleUnloadWarning(false);
      addLoadedFile(data.file.id, data.loadedFile);
      dispatch.addFiles([data.file]);
      dispatch.addFileCollectionFiles([data.fileCollectionFile]);
      dispatch.addFileVersions([data.fileVersion]);
      toggleUploadToaster(false);
      clearUploads();
      onSaveDocument?.(data.file.id);
    } catch (err) {
      toggleUploadToaster(false);
      clearUploads();
      onApiError(err, 'Error uploading document');
    }
  };

  const handleStartTraceRecord = () => {
    toggleMenu(false);
    toggleTraceRecord(true);
  };

  return (
    <>
      <IconButton ref={menuToggleRef} onClick={() => toggleMenu()}>
        <AddIcon />
      </IconButton>
      {isMenuOpen && (
        <NewDocumentMenu
          anchorTo={menuToggleRef.current}
          open={isMenuOpen}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onStartTraceRecord={handleStartTraceRecord}
          onAddFile={handleFileUpload}
          onClose={toggleMenu}
        />
      )}
      {isTraceRecordOpen && (
        <TraceRecordDialog
          onSave={handleFileUpload}
          onClose={() => toggleTraceRecord(false)}
        />
      )}
      <UploadToaster
        open={isUploadToasterOpen}
        files={uploads}
        onClose={toggleUploadToaster}
      />
    </>
  );
}
